import { api, unwrap } from "@lumberset/api-client";
import { dateTime, money, RfqStatus, SupplierStatus } from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  EmptyState,
  Field,
  IconButton,
  Input,
  Prompt,
  SectionHeader,
  SectionRow,
  Table,
  TwoColumn,
  toast,
} from "@lumberset/ui";
import {
  ArrowDownTray,
  DocumentText,
  PaperPlane,
  Plus,
  SquareTwoStack,
  Trash,
} from "@medusajs/icons";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Crumbs } from "../../../components/crumbs";
import { rfqQuery } from "../../../lib/queries";
import { looksLikeEmail, MAX_RECIPIENTS, supplierDetail, supplierName } from "../../../lib/rfq";

export const Route = createFileRoute("/_app/rfqs/$id")({
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData(rfqQuery(params.id)),
  component: RfqDetail,
});

type Recipient = Awaited<
  ReturnType<NonNullable<ReturnType<typeof rfqQuery>["queryFn"]>>
>["recipients"][number];

function RfqDetail() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery({
    ...rfqQuery(id),
    // Replies arrive while the Builder is looking. Keep an open RFQ fresh.
    refetchInterval: (query) => (query.state.data?.rfq.status === "sent" ? 15_000 : false),
  });
  const { rfq, projectName, takeoff, recipients } = data;
  const [confirming, setConfirming] = useState<"send" | "close" | null>(null);
  const [removing, setRemoving] = useState<Recipient | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["rfqs"] });
  const fail = (error: Error) => toast.error(error.message);

  const closed = rfq.status === "closed";
  const pending = recipients.filter((recipient) => recipient.status === "pending");
  const replied = recipients
    .filter((recipient) => recipient.status === "responded" && recipient.totalCents !== null)
    .sort((a, b) => (a.totalCents ?? 0) - (b.totalCents ?? 0));
  const lowest = replied[0]?.totalCents ?? null;

  const send = useMutation({
    mutationFn: () => unwrap(api.rfqs[":id"].send.$post({ param: { id } })),
    onSuccess: async ({ sent, failed }) => {
      await refresh();
      if (failed > 0) {
        toast.error(`${failed} ${failed === 1 ? "email" : "emails"} could not be sent`, {
          description: `${sent} sent. Send again to retry the rest.`,
        });
      } else {
        toast.success(`Sent to ${sent} ${sent === 1 ? "yard" : "yards"}`, {
          description: "You will hear here as their quotes come in.",
        });
      }
    },
    onError: fail,
  });

  const close = useMutation({
    mutationFn: () => unwrap(api.rfqs[":id"].close.$post({ param: { id } })),
    onSuccess: async () => {
      await refresh();
      toast.success("RFQ closed", { description: "Yards can no longer reply." });
    },
    onError: fail,
  });

  const add = useMutation({
    mutationFn: (json: { email: string; name: string | null; company: string | null }) =>
      unwrap(api.rfqs[":id"].recipients.$post({ param: { id }, json })),
    onSuccess: async ({ recipient }) => {
      await refresh();
      toast.success(`${supplierName(recipient)} added`, {
        description: rfq.status === "sent" ? "They get their email on the next send." : undefined,
      });
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (rid: string) =>
      unwrap(api.rfqs[":id"].recipients[":rid"].$delete({ param: { id, rid } })),
    onSuccess: async () => {
      await refresh();
      setRemoving(null);
    },
    onError: fail,
  });

  function addYard(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    const email = String(values.get("email") ?? "")
      .trim()
      .toLowerCase();
    if (!looksLikeEmail(email)) {
      toast.error("That does not look like an email address.");
      return;
    }
    add.mutate(
      {
        email,
        name: String(values.get("name") ?? "").trim() || null,
        company: String(values.get("company") ?? "").trim() || null,
      },
      { onSuccess: () => form.reset() },
    );
  }

  async function copyLink(recipient: Recipient) {
    await navigator.clipboard.writeText(recipient.publicUrl);
    toast.success("Link copied", { description: `For ${supplierName(recipient)}.` });
  }

  return (
    <>
      <Crumbs trail={[{ label: "RFQs", to: "/rfqs" }, { label: projectName }]} />

      <TwoColumn
        main={
          <>
            <Container className="divide-y p-0">
              <SectionHeader
                level="h1"
                title={projectName}
                description={
                  rfq.sentAt
                    ? `Sent ${dateTime(rfq.sentAt)}${closed ? `, closed ${dateTime(rfq.closedAt)}` : ""}`
                    : "Not sent yet"
                }
                actions={
                  <>
                    <RfqStatus status={rfq.status} />
                    {closed ? null : (
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => setConfirming("close")}
                      >
                        Close
                      </Button>
                    )}
                    <Button
                      size="small"
                      disabled={closed || pending.length === 0}
                      isLoading={send.isPending}
                      onClick={() => setConfirming("send")}
                    >
                      <PaperPlane />{" "}
                      {!rfq.sentAt
                        ? "Send"
                        : pending.length > 0
                          ? `Send to ${pending.length} more`
                          : "All sent"}
                    </Button>
                  </>
                }
              />
            </Container>

            <Container className="divide-y overflow-hidden p-0">
              <SectionHeader
                title="Yards"
                description="Each yard has its own link and sees only its own reply."
              />
              {recipients.length === 0 ? (
                <EmptyState
                  className="py-10"
                  title="No yards yet"
                  description="Add the yards you want quotes from, then send."
                />
              ) : (
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Yard</Table.HeaderCell>
                      <Table.HeaderCell>Status</Table.HeaderCell>
                      <Table.HeaderCell className="max-md:hidden">Reply</Table.HeaderCell>
                      <Table.HeaderCell className="text-right">Total</Table.HeaderCell>
                      <Table.HeaderCell>
                        <span className="sr-only">Actions</span>
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {recipients.map((recipient) => (
                      <Table.Row key={recipient.id}>
                        <Table.Cell>
                          <div className="txt-compact-small-plus max-w-[40vw] truncate text-ui-fg-base">
                            {supplierName(recipient)}
                          </div>
                          <div className="txt-compact-xsmall max-w-[40vw] truncate text-ui-fg-muted">
                            {supplierDetail(recipient)}
                          </div>
                        </Table.Cell>
                        <Table.Cell className="whitespace-nowrap">
                          <SupplierStatus status={recipient.status} />
                          <div className="txt-compact-xsmall mt-1 text-ui-fg-muted">
                            {recipient.respondedAt
                              ? dateTime(recipient.respondedAt)
                              : recipient.viewedAt
                                ? dateTime(recipient.viewedAt)
                                : recipient.sentAt
                                  ? dateTime(recipient.sentAt)
                                  : null}
                          </div>
                        </Table.Cell>
                        <Table.Cell className="max-md:hidden">
                          <div className="flex max-w-md flex-col gap-y-1 py-2">
                            {recipient.notes ? (
                              <p className="txt-small whitespace-pre-line text-ui-fg-subtle">
                                {recipient.notes}
                              </p>
                            ) : null}
                            {recipient.quoteFileUrl ? (
                              <a
                                href={recipient.quoteFileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="txt-compact-small inline-flex items-center gap-x-1.5 text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
                              >
                                <DocumentText /> {recipient.quoteFileName ?? "Quote file"}
                              </a>
                            ) : null}
                          </div>
                        </Table.Cell>
                        <Table.Cell className="tabular whitespace-nowrap text-right text-ui-fg-base">
                          {recipient.totalCents !== null
                            ? money(recipient.totalCents, recipient.currency)
                            : "-"}
                        </Table.Cell>
                        <Table.Cell className="w-[1%] whitespace-nowrap">
                          <div className="flex items-center justify-end gap-x-1">
                            {rfq.status === "draft" ? null : (
                              <IconButton
                                aria-label={`Copy link for ${supplierName(recipient)}`}
                                onClick={() => copyLink(recipient)}
                              >
                                <SquareTwoStack />
                              </IconButton>
                            )}
                            {closed ? null : (
                              <IconButton
                                aria-label={`Remove ${supplierName(recipient)}`}
                                onClick={() => setRemoving(recipient)}
                              >
                                <Trash />
                              </IconButton>
                            )}
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}

              {closed ? null : recipients.length >= MAX_RECIPIENTS ? (
                <p className="txt-small px-6 py-3 text-ui-fg-muted">
                  That is the most yards one RFQ can go to.
                </p>
              ) : (
                <form
                  onSubmit={addYard}
                  className="grid gap-4 px-6 py-5 sm:grid-cols-[1.4fr_1fr_1fr_auto] sm:items-end"
                >
                  <Field label="Email">
                    {(control) => (
                      <Input
                        {...control}
                        name="email"
                        type="email"
                        required
                        autoComplete="off"
                        placeholder="quotes@yard.com"
                      />
                    )}
                  </Field>
                  <Field label="Contact" optional>
                    {(control) => <Input {...control} name="name" autoComplete="off" />}
                  </Field>
                  <Field label="Yard" optional>
                    {(control) => <Input {...control} name="company" autoComplete="off" />}
                  </Field>
                  <Button
                    type="submit"
                    variant="secondary"
                    isLoading={add.isPending}
                    className="max-sm:w-full"
                  >
                    <Plus /> Add yard
                  </Button>
                </form>
              )}
            </Container>

            {replied.length > 0 ? (
              <Container className="divide-y overflow-hidden p-0">
                <SectionHeader
                  title="Replies side by side"
                  description="Lowest first. Check what each yard included before choosing."
                />
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Yard</Table.HeaderCell>
                      <Table.HeaderCell className="text-right">Total</Table.HeaderCell>
                      <Table.HeaderCell className="text-right">Above lowest</Table.HeaderCell>
                      <Table.HeaderCell className="max-sm:hidden">Quote file</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {replied.map((recipient) => {
                      const total = recipient.totalCents ?? 0;
                      const above = lowest === null ? 0 : total - lowest;
                      return (
                        <Table.Row key={recipient.id}>
                          <Table.Cell>
                            <div className="txt-compact-small-plus max-w-[40vw] truncate text-ui-fg-base">
                              {supplierName(recipient)}
                            </div>
                            <div className="txt-compact-xsmall text-ui-fg-muted">
                              {dateTime(recipient.respondedAt)}
                            </div>
                          </Table.Cell>
                          <Table.Cell className="tabular whitespace-nowrap text-right text-ui-fg-base">
                            {money(total, recipient.currency)}
                          </Table.Cell>
                          <Table.Cell className="tabular whitespace-nowrap text-right">
                            {above === 0 ? (
                              <span className="text-ui-tag-green-text">Lowest</span>
                            ) : (
                              `+${money(above, recipient.currency)}`
                            )}
                          </Table.Cell>
                          <Table.Cell className="max-sm:hidden">
                            {recipient.quoteFileUrl ? (
                              <a
                                href={recipient.quoteFileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-x-1.5 text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
                              >
                                <ArrowDownTray /> {recipient.quoteFileName ?? "Download"}
                              </a>
                            ) : (
                              "-"
                            )}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              </Container>
            ) : null}
          </>
        }
        side={
          <>
            <Container className="divide-y p-0">
              <SectionHeader title="Takeoff" />
              <SectionRow label="Project">
                <Link
                  to="/projects/$id"
                  params={{ id: rfq.projectId }}
                  className="text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
                >
                  {projectName}
                </Link>
              </SectionRow>
              <SectionRow label="Sent">
                {takeoff.revision !== null
                  ? `The takeoff as it stood when sent (version ${takeoff.revision})`
                  : null}
              </SectionRow>
              <SectionRow label="Materials to price">{takeoff.lineCount}</SectionRow>
              {takeoff.runId ? (
                <div className="flex flex-wrap gap-2 px-6 py-4">
                  <Button size="small" variant="secondary" asChild>
                    <Link
                      to="/projects/$id/takeoffs/$runId"
                      params={{ id: rfq.projectId, runId: takeoff.runId }}
                    >
                      <DocumentText /> Open takeoff
                    </Link>
                  </Button>
                  <Button size="small" variant="secondary" asChild>
                    <a href={`/api/v1/takeoffs/${takeoff.runId}/pdf`} download>
                      <ArrowDownTray /> Download PDF
                    </a>
                  </Button>
                </div>
              ) : (
                <p className="txt-small px-6 py-4 text-ui-fg-muted">
                  The takeoff this RFQ was sent with is no longer available.
                </p>
              )}
            </Container>

            <Container className="divide-y p-0">
              <SectionHeader title="Message to the yards" />
              <p className="txt-small whitespace-pre-line px-6 py-4 text-ui-fg-subtle">
                {rfq.message || "No message was included."}
              </p>
            </Container>
          </>
        }
      />

      <Prompt open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>
              {confirming === "send"
                ? `Send to ${pending.length} ${pending.length === 1 ? "yard" : "yards"}?`
                : "Close this RFQ?"}
            </Prompt.Title>
            <Prompt.Description>
              {confirming === "send"
                ? "Each gets an email with a link to the material list and a form to reply. Yards already sent to are not emailed again."
                : "Yards can still open their link but can no longer reply. This cannot be undone."}
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Not yet</Prompt.Cancel>
            <Prompt.Action
              tone={confirming === "send" ? "primary" : "danger"}
              onClick={() => (confirming === "send" ? send.mutate() : close.mutate())}
            >
              {confirming === "send" ? "Send RFQ" : "Close RFQ"}
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>

      <Prompt open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Remove {removing ? supplierName(removing) : "this yard"}?</Prompt.Title>
            <Prompt.Description>
              Their link stops working
              {removing?.status === "responded" ? ", and their reply goes with them" : ""}.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep them</Prompt.Cancel>
            <Prompt.Action onClick={() => removing && remove.mutate(removing.id)}>
              Remove
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  );
}
